import React, { useState, useEffect } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { StickyDataGrid, Container, TableContainer } from "./styles";

// Custom overlay components for empty states
function CustomNoRowsOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        minHeight: "400px",
        backgroundColor: "white",
        color: "#01151D",
        fontFamily: "Source Sans Pro",
        fontWeight: 400,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: "20px",
          fontFamily: "Source Sans Pro",
          fontWeight: 400,
        }}
      >
        No results were found
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: "16px",
          fontFamily: "Source Sans Pro",
          fontWeight: 400,
        }}
      >
        Try adjusting your search or filters.
      </Typography>
    </Stack>
  );
}

// Custom loading overlay
function CustomLoadingOverlay() {
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        minHeight: "400px",
        backgroundColor: "white",
      }}
    >
      <LinearProgress sx={{ width: "200px" }} />
      <Typography
        variant="body2"
        sx={{
          fontSize: "16px",
          fontFamily: "Source Sans Pro",
          fontWeight: 400,
          color: "#01151D",
        }}
      >
        Loading data...
      </Typography>
    </Stack>
  );
}

/**
 * SolsDataGrid - A generic, reusable data grid component based on MUI DataGridPro
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.columns - Array of column definitions for the grid
 * @param {Array} props.rows - Array of data rows to display
 * @param {Function} props.getRowId - Function to get unique row ID (default: row => row.id)
 * @param {Boolean} props.loading - Loading state indicator
 * @param {Number} props.rowCount - Total number of rows (for server-side pagination)
 * @param {Boolean} props.paginationMode - Pagination mode: 'server' or 'client' (default: 'client')
 * @param {Function} props.onPageChange - Callback for page change (page, pageSize)
 * @param {Function} props.onSortModelChange - Callback for sort model change
 * @param {Boolean} props.sortingMode - Sorting mode: 'server' or 'client' (default: 'client')
 * @param {Array} props.pageSizeOptions - Available page size options (default: [10, 20, 30, 40, 50])
 * @param {Number} props.initialPageSize - Initial page size (default: 10)
 * @param {Object} props.columnVisibilityModel - Column visibility configuration
 * @param {Function} props.onColumnVisibilityModelChange - Callback for column visibility change
 * @param {Function} props.onColumnWidthChange - Callback for column width change
 * @param {Function} props.onColumnOrderChange - Callback for column order change
 * @param {Object} props.pinnedColumns - Pinned columns configuration { left: [], right: [] }
 * @param {Boolean} props.disableColumnFilter - Disable column filtering (default: true)
 * @param {Boolean} props.disableDensitySelector - Disable density selector (default: true)
 * @param {Boolean} props.disableColumnMenu - Disable column menu (default: true)
 * @param {Boolean} props.showCellVerticalBorder - Show vertical borders between cells (default: false)
 * @param {String} props.height - Grid height (default: 'calc(100vh - 190px)'). Use 'auto' to fit row count.
 * @param {Boolean} props.autoHeight - When true, grid height fits the number of rows (overrides height).
 * @param {Object} props.customStyles - Additional custom styles
 * @param {Object} props.sx - MUI sx prop for custom styling (will be merged with default checkbox styles)
 * @param {Function} props.getCellClassName - Function to get custom cell class names
 * @param {Boolean} props.checkboxSelection - Enable checkbox selection (default: false)
 * @param {Array} props.rowSelectionModel - Array of selected row IDs
 * @param {Function} props.onRowSelectionModelChange - Callback when row selection changes
 * 
 * @note The 'actions' column (field: 'actions') is automatically sticky on the right side
 */
const SolsDataGrid = (props) => {
  const {
    // Required props
    columns = [],
    rows = [],
    
    // Row identification
    getRowId = (row) => row.id,
    
    // Loading state
    loading = false,
    
    // Pagination props
    rowCount,
    paginationMode = "client",
    onPageChange = () => {},
    pageSizeOptions = [10, 20, 30, 40, 50],
    initialPageSize = 10,
    
    // Sorting props
    sortingMode = "client",
    onSortModelChange = () => {},
    
    // Column configuration
    columnVisibilityModel = {},
    onColumnVisibilityModelChange = () => {},
    onColumnWidthChange = () => {},
    onColumnOrderChange = () => {},
    
    // Pinned columns
    pinnedColumns = { left: [], right: [] },
    
    // Grid features
    disableColumnFilter = true,
    disableDensitySelector = true,
    disableColumnMenu = true,
    showCellVerticalBorder = false,
    
    // Row selection
    checkboxSelection = false,
    rowSelectionModel = [],
    onRowSelectionModelChange = () => {},
    
    // Styling
    height = "calc(100vh - 190px)",
    autoHeight = false,
    customStyles = {},
    sx = {},
    
    // Cell customization
    getCellClassName = () => "",
    
    // Additional props
    ...otherProps
  } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: initialPageSize,
  });

  const [columnWidths, setColumnWidths] = useState({});
  
  // Ensure actions column is always visible in the visibility model
  const ensureActionsVisible = (model) => {
    return {
      ...model,
      actions: true, // Always keep actions column visible
    };
  };

  const [internalColumnVisibilityModel, setInternalColumnVisibilityModel] = useState(
    ensureActionsVisible(columnVisibilityModel)
  );

  // Update internal state when prop changes, ensuring actions remains visible
  useEffect(() => {
    setInternalColumnVisibilityModel(ensureActionsVisible(columnVisibilityModel));
  }, [columnVisibilityModel]);

  // Handle page change
  const handlePageChange = ({ page, pageSize }) => {
    setPaginationModel({ page, pageSize });
    onPageChange(page, pageSize);
  };

  // Handle column width change
  const handleColumnWidthChange = (params) => {
    setColumnWidths((prevWidths) => ({
      ...prevWidths,
      [params.colDef.field]: params.width,
    }));
    onColumnWidthChange(params);
  };

  // Handle column visibility change
  const handleColumnVisibilityModelChange = (newModel) => {
    const modelWithActions = ensureActionsVisible(newModel);
    setInternalColumnVisibilityModel(modelWithActions);
    onColumnVisibilityModelChange(modelWithActions);
  };

  // Determine effective row count
  const effectiveRowCount = paginationMode === "server" ? rowCount : rows.length;

  // Use provided height or default; when autoHeight, container and grid size to content
  const useAutoHeight = autoHeight || height === "auto";
  const gridHeight = useAutoHeight ? "auto" : (height || "calc(100vh - 190px)");

  // Define checkbox consistency styling that will be merged with any custom sx props
  const checkboxConsistencySx = {
    // Force checkbox consistency between header and rows
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
      color: "#56656B !important",
      padding: "9px !important",
      width: "40px !important",
      height: "40px !important",
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root .MuiSvgIcon-root": {
      fontSize: "20px !important",
      width: "20px !important",
      height: "20px !important",
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked": {
      color: "#09779E !important",
    },
    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate": {
      color: "#09779E !important",
    },
    "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
      color: "#56656B !important",
      padding: "9px !important",
      width: "40px !important",
      height: "40px !important",
    },
    "& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-checked": {
      color: "#09779E !important",
    },
    // Merge with any custom sx passed from parent
    ...sx,
  };

  return (
    <Container style={{ height: gridHeight }} $autoHeight={useAutoHeight}>
      <TableContainer $autoHeight={useAutoHeight}>
        <StickyDataGrid
          autoHeight={useAutoHeight}
          // Core data
          columns={columns}
          rows={rows}
          getRowId={getRowId}
          
          // Loading state
          loading={loading}
          
          // Pagination
          pagination
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePageChange}
          pageSizeOptions={pageSizeOptions}
          rowCount={effectiveRowCount}
          
          // Sorting
          sortingMode={sortingMode}
          onSortModelChange={onSortModelChange}
          
          // Row selection
          checkboxSelection={checkboxSelection}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          disableRowSelectionOnClick
          
          // Column configuration
          columnVisibilityModel={internalColumnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnWidthChange={handleColumnWidthChange}
          onColumnOrderChange={onColumnOrderChange}
          
          // Pinned columns
          pinnedColumns={pinnedColumns}
          initialState={{
            pinnedColumns: pinnedColumns,
            pagination: {
              paginationModel: { pageSize: initialPageSize, page: 0 },
            },
            columns: {
              columnVisibilityModel: {
                // Ensure pinned columns are always visible
                leaseCode: true,
                leaseName: true,
                actions: true,
              },
            },
          }}
          
          // Grid features
          disableColumnFilter={disableColumnFilter}
          disableDensitySelector={disableDensitySelector}
          disableColumnMenu={disableColumnMenu}
          showCellVerticalBorder={showCellVerticalBorder}
          
          // Performance optimization (disable virtualization when autoHeight so grid can size to rows)
          disableVirtualization={useAutoHeight}
          scrollbarSize={17}
          pinnedColumnsSectionSeparator="border"
          rowBuffer={5}
          columnBuffer={2}
          rowThreshold={3}
          columnThreshold={3}
          
          // Custom overlays
          slots={{
            toolbar: null,
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: CustomLoadingOverlay,
          }}
          
          // Cell customization
          getCellClassName={getCellClassName}
          
          // Styling with checkbox consistency
          sx={checkboxConsistencySx}
          
          // Additional props
          {...otherProps}
        />
      </TableContainer>
    </Container>
  );
};

export default SolsDataGrid;
